import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResult } from '../../../../shared/models/api-result.model';
import {
  Offer,
  CreateOffer,
  UpdateOffer,
  OfferStatus,
} from '../../../models/offer.model';
import { OfferService } from '../../../services/offer.service';
import { ProductService } from '../../../services/product.service';
import { PaymentFrequencyService } from '../../../services/payment-frequency.service';
import { LateFeePolicyService } from '../../../services/late-fee-policy.service';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { DropdownOption } from '../../../../shared/models/dropdowm.model';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { installmentTotalValidator } from '../../../../shared/validators/installment.validator';

@Component({
  selector: 'app-offer-create-or-update',
  standalone: true,
  templateUrl: './offer-create-or-update.component.html',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    ProgressSpinnerModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    ToastModule,
    ToolbarModule,
  ],
})
export class OfferCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private offerService = inject(OfferService);
  private productService = inject(ProductService);
  private paymentFrequencyService = inject(PaymentFrequencyService);
  private lateFeePolicyService = inject(LateFeePolicyService);
  private formGroupService = inject(FormGroupService);

  formGroup: FormGroup = this.fb.group(
    {
      id: [null],
      name: [null, [Validators.required, Validators.maxLength(255)]],
      productId: [{ value: null, disabled: true }, [Validators.required]],
      paymentFrequencyId: [
        { value: null, disabled: true },
        [Validators.required],
      ],
      downPayment: [
        0,
        [Validators.required, Validators.min(0.01), Validators.max(1000000)],
      ],
      installmentAmount: [
        0,
        [Validators.required, Validators.min(0.01), Validators.max(1000000)],
      ],
      installmentCount: [
        1,
        [Validators.required, Validators.min(1), Validators.max(1000)],
      ],
      fullPrice: [0],
      cashPrice: [
        0,
        [Validators.required, Validators.min(0.01), Validators.max(1000000)],
      ],
      lateFeePolicyId: [null],
      status: [OfferStatus.Draft, [Validators.required]],
      interestRate: [
        0,
        [Validators.required, Validators.min(0), Validators.max(10000)],
      ],
    },
    {
      validators: [installmentTotalValidator],
    },
  );

  offerId = signal<string | null>(null);
  loading = signal(false);

  statusOptions = this.offerService.getOfferStatusOptions();

  productsDropdown = signal<DropdownOption[]>([]);
  paymentFrequenciesDropdown = signal<DropdownOption[]>([]);
  productSelectLoading = signal(false);
  lateFeePoliciesDropdown = signal<DropdownOption[]>([]);
  paymentFrequencySelectLoading = signal(false);
  lateFeePolicySelectLoading = signal(false);

  ngOnInit(): void {
    this.formGroup.valueChanges.subscribe(() => this.updateFullPrice());
    this.updateFullPrice();

    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.offerId.set(id);
      this.loadData(id);
    } else {
      this.loadProductsDropdown();
      this.loadPaymentFrequenciesDropdown();
      this.loadLateFeePoliciesDropdown();
      this.formGroup.get('productId')?.enable({ emitEvent: false });
      this.formGroup.get('paymentFrequencyId')?.enable({ emitEvent: false });
    }
  }

  onCurrencyInput(controlName: string, event: any): void {
    this.formGroupService.onCurrencyInput(this.formGroup, controlName, event);
  }

  private updateFullPrice(): void {
    const { installmentAmount, installmentCount, downPayment } =
      this.formGroup.getRawValue();
    const fullPrice =
      Number(installmentAmount || 0) * Number(installmentCount || 0) +
      Number(downPayment || 0);

    this.formGroup
      .get('fullPrice')
      ?.setValue(fullPrice.toFixed(2), { emitEvent: false });
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.offerId();
    const formValue = this.formGroup.getRawValue();

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    if (id) {
      const body: UpdateOffer = {
        status: formValue.status as OfferStatus,
      };
      request$ = this.offerService.updateStatus(id, body);
    } else {
      delete formValue.id;
      formValue.fullPrice = Number(formValue.fullPrice);
      formValue.productId = formValue.productId?.value;
      formValue.paymentFrequencyId = formValue.paymentFrequencyId?.value;
      formValue.lateFeePolicyId = formValue.lateFeePolicyId?.value ?? null;
      request$ = this.offerService.create(formValue as CreateOffer);
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Oferta salva com sucesso',
          life: 2000,
        });

        this.router.navigate(['/offers']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors?.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a oferta',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.offerService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const data = response.data as Offer;
        this.formGroup.patchValue(data);
        this.formGroup.get('status')?.setValue(OfferStatus[data.status]);

        const product = {
          label: data.productName,
          value: data.productId,
        } as DropdownOption;
        this.loadProductsDropdown(product);

        const paymentFrequency = {
          label: data.paymentFrequencyName,
          value: data.paymentFrequencyId,
        } as DropdownOption;
        this.loadPaymentFrequenciesDropdown(paymentFrequency);

        this.formGroup.get('lateFeePolicyId')?.setValue(null);
        const lateFeePolicy = data.lateFeePolicyId
          ? ({
              label: data.lateFeePolicyName,
              value: data.lateFeePolicyId,
            } as DropdownOption)
          : null;
        this.loadLateFeePoliciesDropdown(lateFeePolicy);

        this.disableNonStatusFields();
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a oferta',
            life: 3000,
          });
        }
      },
    });
  }

  private loadProductsDropdown(product: DropdownOption | null = null): void {
    this.productSelectLoading.set(true);

    this.productService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        this.productSelectLoading.set(false);

        const options = response.data as DropdownOption[];
        this.productsDropdown.set(options);

        if (product) this.formGroup.get('productId')?.setValue(product);
      },
      error: () => {
        this.productSelectLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar os produtos',
          life: 3000,
        });
      },
    });
  }

  private loadPaymentFrequenciesDropdown(
    paymentFrequency: DropdownOption | null = null,
  ): void {
    this.paymentFrequencySelectLoading.set(true);

    this.paymentFrequencyService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        this.paymentFrequencySelectLoading.set(false);

        const options = response.data as DropdownOption[];
        this.paymentFrequenciesDropdown.set(options);

        if (paymentFrequency)
          this.formGroup.get('paymentFrequencyId')?.setValue(paymentFrequency);
      },
      error: () => {
        this.paymentFrequencySelectLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar as frequências de pagamento',
          life: 3000,
        });
      },
    });
  }

  private loadLateFeePoliciesDropdown(
    lateFeePolicy: DropdownOption | null = null,
  ): void {
    this.lateFeePolicySelectLoading.set(true);

    this.lateFeePolicyService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        this.lateFeePolicySelectLoading.set(false);

        const options = response.data as DropdownOption[];
        this.lateFeePoliciesDropdown.set(options);

        if (lateFeePolicy)
          this.formGroup.get('lateFeePolicyId')?.setValue(lateFeePolicy);
      },
      error: () => {
        this.lateFeePolicySelectLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Falha',
          detail: 'Ocorreu um erro ao carregar as multas',
          life: 3000,
        });
      },
    });
  }

  private disableNonStatusFields(): void {
    Object.keys(this.formGroup.controls).forEach((key) => {
      if (key !== 'status') {
        this.formGroup.get(key)?.disable({ emitEvent: false });
      }
    });
  }
}
